import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "../shared";
import { lendersApi, debtApi } from "../../lib/api";
import { toast } from "sonner";
import { Landmark, Search } from "lucide-react";

export const LenderListSection = ({ dealKey, dealName, tracked, onImported }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [byType, setByType] = useState({});
  const [q, setQ] = useState("");
  const [type, setType] = useState("All");
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [list, stats] = await Promise.all([lendersApi.list({ ...(type !== "All" ? { type } : {}), ...(q ? { q } : {}), limit: 500 }), lendersApi.stats()]);
    setRows(list); setTotal(stats.total || 0); setByType(stats.byType || {});
  }, [q, type]);
  useEffect(() => { const t = setTimeout(() => load().catch(() => toast.error("Could not load the lender list")), 250); return () => clearTimeout(t); }, [load]);

  const types = useMemo(() => ["All", ...Object.entries(byType).sort((a, b) => b[1] - a[1]).map(([t]) => t)], [byType]);
  const trackedIds = useMemo(() => new Set(tracked.map((l) => l.lenderId).filter(Boolean)), [tracked]);
  const trackedEmails = useMemo(() => new Set(tracked.map((l) => (l.email || "").toLowerCase()).filter(Boolean)), [tracked]);
  const isTracked = (r) => trackedIds.has(r.id) || (r.email && trackedEmails.has(r.email.toLowerCase()));
  const picked = rows.filter((r) => sel[r.id]);

  const run = async (payload, key) => {
    setBusy(key);
    try { const r = await debtApi.importLenders({ dealKey, dealName, ...payload }); setSel({}); onImported(); toast.success(`${r.imported} lender(s) added to the pipeline (${r.skipped} already tracked)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(""); }
  };

  return (
    <Card title={`Lender list — ${total.toLocaleString()} contacts in the Debt Lenders CRM`} testid="dl-list">
      <div className="cm-toolbar">
        <input className="inp cm-search" placeholder="Search banker, bank, email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dll-search" />
        <select className="inp cm-inline" value={type} onChange={(e) => setType(e.target.value)} data-testid="dll-type">
          {types.map((t) => <option key={t} value={t}>{t === "All" ? `All lender types (${total.toLocaleString()})` : `${t} (${(byType[t] || 0).toLocaleString()})`}</option>)}
        </select>
        <button className="btn btn-primary" onClick={() => picked.length ? run({ ids: picked.map((x) => x.id) }, "sel") : toast.error("Tick at least one lender")} disabled={busy === "sel"} data-testid="dll-add-selected">
          <Landmark size={13} />{busy === "sel" ? "Adding…" : `Add ${picked.length || ""} to pipeline`}
        </button>
        <button className="btn" onClick={() => run({ type: type === "All" ? null : type, limit: 500 }, "all")} disabled={busy === "all"} data-testid="dll-add-all">
          <Search size={13} />{busy === "all" ? "Adding…" : `Add all ${type === "All" ? "" : type + " "}matching`}
        </button>
      </div>
      <div className="cm-table-wrap">
        <table className="tbl inv-tbl" data-testid="dll-table">
          <thead><tr>
            <th><input type="checkbox" checked={rows.length > 0 && rows.every((r) => sel[r.id])} onChange={(e) => setSel(e.target.checked ? Object.fromEntries(rows.map((r) => [r.id, true])) : {})} data-testid="dll-check-all" /></th>
            <th>Banker</th><th>Bank / lender</th><th>Title</th><th>Email</th><th>Phone</th><th>Type</th><th>Status</th><th>In pipeline</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="cm-lead-row" data-testid={`dll-row-${r.id}`}>
                <td><input type="checkbox" checked={!!sel[r.id]} onChange={() => setSel((s) => ({ ...s, [r.id]: !s[r.id] }))} data-testid={`dll-check-${r.id}`} /></td>
                <td>{r.name || "—"}</td><td><b>{r.company || "—"}</b></td><td style={{ color: "var(--text2)" }}>{r.title || "—"}</td>
                <td style={{ color: "var(--text2)" }}>{r.email || "—"}</td><td className="mono">{r.phone || "—"}</td>
                <td><span className="tag">{r.type || "—"}</span></td><td style={{ color: "var(--text3)", fontSize: 11 }}>{r.status || "New"}</td>
                <td>{isTracked(r) ? <span className="pill go">Tracked</span> : <span className="cm-sub">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="cm-empty-block">No lenders match. Add bankers in the Debt Lenders tab and they show up here.</div>}
      </div>
      <div className="cm-empty" style={{ marginTop: 10 }}>Adding a banker creates a lender in this deal's debt pipeline at stage Contacted — your Debt Lenders CRM record is untouched.</div>
    </Card>
  );
};
